import React, { useState, useEffect } from "react";
import { Search, Check, X, Crown } from "lucide-react";
import { useDarkMode } from "../../context/DarkModeContext";

// Type Definitions
interface User {
  id: string;
  name: string;
  email: string;
}

interface WhitelistRequest {
  id: string;
  userId: string;
  user: User;
  company: string;
  position: string;
  expectedVolume: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  adminNotes?: string;
}

interface WhitelistedUser {
  id: string;
  userId: string;
  user: User;
  whitelistedBy: string;
  whitelistedByUser: User;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
}

interface Filters {
  status: string;
  search: string;
}

const API_BASE = import.meta.env.VITE_API_BASE || "";

const WhitelistManagement: React.FC = () => {
  const { isDark } = useDarkMode();
  const [requests, setRequests] = useState<WhitelistRequest[]>([]);
  const [whitelistedUsers, setWhitelistedUsers] = useState<WhitelistedUser[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"requests" | "whitelisted">(
    "requests",
  );
  const [filters, setFilters] = useState<Filters>({
    status: "pending",
    search: "",
  });

  useEffect(() => {
    fetchData();
  }, [activeTab, filters]);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    try {
      if (activeTab === "requests") {
        const response = await fetch(
          `${API_BASE}/whitelist/admin/requests?status=${filters.status}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch requests");
        const data = await response.json();
        setRequests(data.requests || data || []);
      } else {
        const response = await fetch(`${API_BASE}/whitelist/admin/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch whitelisted users");
        const data = await response.json();
        setWhitelistedUsers(data.whitelistedUsers || data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (
    requestId: string,
    durationDays: number,
  ): Promise<void> => {
    try {
      const response = await fetch(
        `${API_BASE}/whitelist/admin/requests/${requestId}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ durationDays }),
        },
      );

      if (response.ok) {
        fetchData();
      } else {
        console.error("Failed to approve request");
      }
    } catch (error) {
      console.error("Error approving request:", error);
    }
  };

  const handleReject = async (
    requestId: string,
    reason: string,
  ): Promise<void> => {
    try {
      const response = await fetch(
        `${API_BASE}/whitelist/admin/requests/${requestId}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ reason }),
        },
      );

      if (response.ok) {
        fetchData();
      } else {
        console.error("Failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
    }
  };

  const handleRevoke = async (
    userId: string,
    reason: string,
  ): Promise<void> => {
    try {
      const response = await fetch(
        `${API_BASE}/whitelist/admin/users/${userId}/revoke`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ reason }),
        },
      );

      if (response.ok) {
        fetchData();
      } else {
        console.error("Failed to revoke whitelist");
      }
    } catch (error) {
      console.error("Error revoking whitelist:", error);
    }
  };

  // Filter requests based on search term
  const filteredRequests = requests.filter(
    (request) =>
      request.user.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      request.user.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      request.company.toLowerCase().includes(filters.search.toLowerCase()),
  );

  // Filter whitelisted users based on search term
  const filteredWhitelistedUsers = whitelistedUsers.filter(
    (user) =>
      user.user.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.user.email.toLowerCase().includes(filters.search.toLowerCase()),
  );

  const displayRequests = filters.search ? filteredRequests : requests;
  const displayWhitelistedUsers = filters.search
    ? filteredWhitelistedUsers
    : whitelistedUsers;

  return (
    <div
      className={`p-6 ${isDark ? "bg-gray-900 min-h-screen" : "bg-gray-50"}`}
    >
      <div className="mb-6">
        <h1
          className={`text-2xl font-bold mb-2 ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          Whitelist Management
        </h1>
        <p className={isDark ? "text-gray-300" : "text-gray-600"}>
          Manage user whitelist requests and permissions
        </p>
      </div>

      {/* Tabs */}
      <div
        className={`border-b mb-6 ${
          isDark ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("requests")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "requests"
                ? isDark
                  ? "border-blue-400 text-blue-400"
                  : "border-blue-500 text-blue-600"
                : isDark
                  ? "border-transparent text-gray-400 hover:text-gray-300"
                  : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Pending Requests ({displayRequests.length})
          </button>
          <button
            onClick={() => setActiveTab("whitelisted")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "whitelisted"
                ? isDark
                  ? "border-blue-400 text-blue-400"
                  : "border-blue-500 text-blue-600"
                : isDark
                  ? "border-transparent text-gray-400 hover:text-gray-300"
                  : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Whitelisted Users ({displayWhitelistedUsers.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div
        className={`p-4 rounded-lg shadow-sm border mb-6 ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search
                size={20}
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  isDark ? "text-gray-400" : "text-gray-400"
                }`}
              />
              <input
                type="text"
                placeholder="Search users..."
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "border-gray-300 text-gray-900"
                }`}
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
          </div>
          {activeTab === "requests" && (
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className={`border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "border-gray-300 text-gray-900"
              }`}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className={`mt-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Loading...
          </p>
        </div>
      ) : activeTab === "requests" ? (
        <div className="space-y-4">
          {displayRequests.length === 0 ? (
            <div className="text-center py-8">
              <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                No requests found
              </p>
            </div>
          ) : (
            displayRequests.map((request) => (
              <div
                key={request.id}
                className={`rounded-lg shadow-sm border p-6 ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {request.user.name}
                    </h3>
                    <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                      {request.user.email}
                    </p>
                    <p
                      className={`text-sm mt-1 ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Requested{" "}
                      {new Date(request.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      request.status === "pending"
                        ? isDark
                          ? "bg-yellow-900/30 text-yellow-300"
                          : "bg-yellow-100 text-yellow-800"
                        : request.status === "approved"
                          ? isDark
                            ? "bg-green-900/30 text-green-300"
                            : "bg-green-100 text-green-800"
                          : isDark
                            ? "bg-red-900/30 text-red-300"
                            : "bg-red-100 text-red-800"
                    }`}
                  >
                    {request.status.charAt(0).toUpperCase() +
                      request.status.slice(1)}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Company
                    </p>
                    <p className={isDark ? "text-white" : "text-gray-900"}>
                      {request.company}
                    </p>
                  </div>
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Position
                    </p>
                    <p className={isDark ? "text-white" : "text-gray-900"}>
                      {request.position}
                    </p>
                  </div>
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Expected Job Volume
                    </p>
                    <p className={isDark ? "text-white" : "text-gray-900"}>
                      {request.expectedVolume} jobs/month
                    </p>
                  </div>
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Reason
                    </p>
                    <p className={isDark ? "text-white" : "text-gray-900"}>
                      {request.reason}
                    </p>
                  </div>
                </div>

                {request.status === "pending" && (
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => handleApprove(request.id, 30)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Check size={16} />
                      Approve (30 days)
                    </button>
                    <button
                      onClick={() => handleApprove(request.id, 90)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Check size={16} />
                      Approve (90 days)
                    </button>
                    <button
                      onClick={() =>
                        handleReject(request.id, "Not meeting criteria")
                      }
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <X size={16} />
                      Reject
                    </button>
                  </div>
                )}

                {request.status !== "pending" && request.adminNotes && (
                  <div
                    className={`mt-4 p-3 rounded-lg ${
                      isDark ? "bg-gray-700" : "bg-gray-50"
                    }`}
                  >
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Admin Notes
                    </p>
                    <p className={isDark ? "text-white" : "text-gray-900"}>
                      {request.adminNotes}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {displayWhitelistedUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                No whitelisted users found
              </p>
            </div>
          ) : (
            displayWhitelistedUsers.map((whitelist) => (
              <div
                key={whitelist.id}
                className={`rounded-lg shadow-sm border p-6 ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown
                        size={20}
                        className={
                          isDark ? "text-yellow-400" : "text-yellow-500"
                        }
                      />
                      <h3
                        className={`text-lg font-semibold ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {whitelist.user.name}
                      </h3>
                    </div>
                    <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                      {whitelist.user.email}
                    </p>
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Whitelisted by {whitelist.whitelistedByUser.name} on{" "}
                      {new Date(whitelist.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-sm font-medium ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Expires
                    </p>
                    <p
                      className={`${
                        whitelist.expiresAt &&
                        new Date(whitelist.expiresAt) < new Date()
                          ? "text-red-600 font-semibold"
                          : isDark
                            ? "text-white"
                            : "text-gray-900"
                      }`}
                    >
                      {whitelist.expiresAt
                        ? new Date(whitelist.expiresAt).toLocaleDateString()
                        : "Never"}
                    </p>
                    {whitelist.expiresAt &&
                      new Date(whitelist.expiresAt) < new Date() && (
                        <p
                          className={`text-xs mt-1 ${
                            isDark ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          Expired
                        </p>
                      )}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {whitelist.notes || "No additional notes"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleRevoke(whitelist.userId, "Admin decision")
                    }
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Revoke Access
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default WhitelistManagement;
